import { Checkbox, Flex, Text } from "@radix-ui/themes";

import {
  buildDescribedBy,
  type ControlProps,
} from "./LayoutSettingsBasicControls.pres";
import type { LayoutSettingControl } from "./layoutSettings.types";

interface HandlesControlProps extends ControlProps {
  control: Extract<LayoutSettingControl, { kind: "handles" }>;
}

export const HandlesControlPresentation = ({
  field,
  controlId,
  descId,
  errorId,
  control,
  onFieldChange,
}: HandlesControlProps) => {
  const hasError = !!field.error;
  const describedBy = buildDescribedBy(descId, errorId, hasError);
  const activeHandles = Array.isArray(field.value)
    ? (field.value as string[])
    : [];
  return (
    // `role=group` does not support aria-invalid, so the invalid state lives
    // on the checkboxes; the fieldset keeps the description association.
    <fieldset aria-describedby={describedBy}>
      <legend>
        <Text size="1" weight="medium">
          {field.descriptor.label}
        </Text>
      </legend>
      <Flex wrap="wrap" gap="3">
        {control.options.map((opt) => {
          // Per-option DOM id so <label htmlFor> associates with each
          // Radix Checkbox unambiguously (a Checkbox renders as a
          // <button role="checkbox">, not an <input>, so Biome's
          // noLabelWithoutControl rule requires an explicit for/id link).
          const optionId = `${controlId}-${opt.value}`;
          return (
            <Flex asChild align="center" gap="1" key={opt.value}>
              <label htmlFor={optionId}>
                <Checkbox
                  id={optionId}
                  size="1"
                  checked={activeHandles.includes(opt.value)}
                  disabled={field.disabled}
                  // The wrapping <label htmlFor={optionId}> plus the
                  // visible `<Text>` below already name the control; an
                  // aria-label here would just duplicate that name.
                  aria-describedby={describedBy}
                  aria-invalid={hasError || undefined}
                  onCheckedChange={(checked) =>
                    onFieldChange(field.descriptor.id, {
                      kind: "handle",
                      value: opt.value,
                      checked: checked === true,
                    })
                  }
                />
                <Text size="1">{opt.label}</Text>
              </label>
            </Flex>
          );
        })}
      </Flex>
    </fieldset>
  );
};
